import { createTheme } from '@mui/material/styles'

declare module '@mui/material/FormHelperText' {
  interface FormHelperTextPropsVariantOverrides {
    overline: true // Add 'overline' as a valid variant
  }
}

// Extend the Components interface to include MuiSlide
declare module '@mui/material/styles' {
  interface Components {
    MuiSlide?: {
      defaultProps?: {
        timeout?: number | { enter?: number; exit?: number }
      }
    }
  }
}

// Define commonly used colors as constants
export const DARK_GREY = 'rgb(54, 54, 54)'
export const LIGHT_GREY = 'rgb(115, 115, 115)'
export const BORDER_GREY = 'rgba(0, 0, 0, 0.2)'

// Common button styles to avoid duplication
const commonButtonStyles = {
  alignItems: 'center' as const,
  justifyContent: 'flex-start' as const,
  lineHeight: 1,
  padding: '5px 15px',
  textTransform: 'none' as const,
  width: '15em',
  '& img, & svg': {
    marginRight: 8,
    width: 24
  }
}

export const poziTheme = createTheme({
  components: {
    MuiButton: {
      defaultProps: {
        variant: 'contained'
      },
      styleOverrides: {
        root: {
          ...commonButtonStyles,
          height: '36px'
        }
      },
      variants: [
        {
          props: { variant: 'outlined' },
          style: {
            borderColor: BORDER_GREY,
            borderRadius: 4,
            borderWidth: 1,
            color: LIGHT_GREY,
            '&:hover': {
              backgroundColor: 'rgba(115, 115, 115, 0.1)',
              borderColor: BORDER_GREY
            }
          }
        }
      ]
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          height: '24px',
          margin: '0px 8px',
          padding: 0,
          '&.Mui-checked': {
            color: LIGHT_GREY
          },
          '&.Mui-unchecked': {
            borderColor: 'rgba(0, 0, 0, 0.6)'
          }
        }
      }
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          marginBottom: 6,
          marginTop: 6,
        }
      }
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          padding: 0,
          margin: 0
        },
        label: {
          fontSize: '0.85rem'
        }
      }
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          color: DARK_GREY,
          '&.Mui-focused': {
            color: DARK_GREY
          }
        }
      }
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          color: 'rgba(0, 0, 0, 0.54)'
        }
      },
      variants: [
        {
          props: { variant: 'overline' }, // Define the custom variant
          style: {
            fontSize: '0.75rem',
            fontWeight: 400,
            letterSpacing: '0.08333em',
            lineHeight: 2.66,
            paddingBottom: 10,
            paddingTop: 10,
            textTransform: 'uppercase'
          }
        }
      ]
    },
    MuiInput: {
      styleOverrides: {
        underline: {
          '&:before': {
            borderBottomColor: DARK_GREY
          },
          '&:hover:not(.Mui-disabled):before': {
            borderBottomColor: DARK_GREY
          },
          '&:after': {
            borderBottomColor: DARK_GREY
          }
        }
      }
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: '0.75rem',
          left: 0,
          transform: 'none'
        }
      }
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          padding: '6px 16px',
          margin: 0,
          '&.Mui-selected': {
            backgroundColor: 'rgba(0, 0, 0, 0.08)',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)'
            }
          }
        }
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        notchedOutline: {
          borderColor: DARK_GREY
        },
        root: {
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: DARK_GREY
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: DARK_GREY
          }
        }
      }
    },
    MuiSlide: {
      defaultProps: {
        timeout: {
          enter: 300,
          exit: 100
        }
      }
    },
    MuiStack: {
      styleOverrides: {
        root: {
          columnGap: 10,
          rowGap: 10
        }
      }
    },
    MuiTextField: {
      defaultProps: {
        variant: 'standard'
      }
    },
    MuiToggleButton: {
      defaultProps: {
        color: 'primary'
      },
      styleOverrides: {
        root: {
          ...commonButtonStyles,
          borderColor: BORDER_GREY,
          borderRadius: 4,
          borderWidth: 1,
          '&:hover': {
            backgroundColor: 'rgb(240, 240, 240)'
          },
          '&.Mui-selected': {
            backgroundColor: 'rgb(215, 215, 215)',
            color: LIGHT_GREY,
            '&:hover': {
              backgroundColor: 'rgb(200, 200, 200)'
            }
          }
        }
      }
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          minHeight: 'initial !important',
          paddingLeft: '15px !important',
          paddingRight: '48px !important',
          '& .MuiButtonBase-root': {
            height: '48px',
            padding: '12px'
          }
        }
      }
    }
  },
  palette: {
    primary: {
      main: 'rgb(112, 146, 92)'
    },
    secondary: {
      main: LIGHT_GREY
    },
    error: {
      main: 'rgba(211, 47, 47, 0.75)'
    },
    text: {
      primary: DARK_GREY
    }
  },
  typography: {
    fontFamily: 'Roboto, Helvetica Arial, sans-serif',
    body1: {
      fontSize: '0.875rem'
    }
  }
})
